import { useEffect, useMemo } from "react"
import { Link } from "react-router"
import { motion, useMotionValue, useSpring, useTransform } from "motion/react"
import { toast } from "sonner"
import {
  ArrowLeft,
  ArrowUpRight,
  Bank,
  Check,
  CheckSquare,
  Coins03,
  CurrencyDollarCircle,
  InfoCircle,
  Plus,
  Trophy01,
  Wallet01,
  ZapFast,
} from "@untitledui/icons"

import type { ApiEstimate, ApiTeam, TeamMemberSummary, TeamSettingsMetadata } from "@/lib/api/types"
import type { PayoutRecord } from "@/lib/api/payouts"
import { usePayouts } from "@/lib/api/use-payouts"
import {
  computeAdvisorStats,
  type WalletEvent,
} from "@/lib/dashboard/advisor-stats"
import { COMPETITION_DEFAULTS } from "@/lib/dashboard/defaults"
import { cx } from "@/lib/utils/cx"

// Wallet surface. Status-driven buckets (Available / Pending / Lost),
// withdraw entry-point, and connected payout methods. Visual grammar
// borrows Revolut: hero balance card with gradient, big tabular numbers,
// generous whitespace, restrained color, date-bucketed transaction feed.

interface WalletViewProps {
  team: ApiTeam | undefined
  estimates: ApiEstimate[]
  // Team roster — needed so competition wins in the wallet resolve with the
  // same Service-Advisor filter the /payouts screen uses. Without it, a
  // non-advisor's scans could steal a daily/first-scan win from the advisor.
  members: TeamMemberSummary[] | undefined
  firstName: string
  currentUserId: number | null
  activeAdvisorCount: number
  backHref: string
}

const FADE_UP = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 240, damping: 26 } },
}
const STAGGER = { staggerChildren: 0.04, delayChildren: 0.04 }

export function WalletView({
  team,
  estimates,
  members,
  firstName,
  currentUserId,
  activeAdvisorCount,
  backHref,
}: WalletViewProps) {
  const meta = (team?.metadata ?? {}) as TeamSettingsMetadata
  const stats = useMemo(
    () =>
      computeAdvisorStats({
        estimates,
        members,
        currentUserId,
        currentUserFirstName: firstName,
        goalsRewards: meta.goalsRewards,
        baseline: meta.serviceLane,
        activeAdvisorCount,
      }),
    [estimates, members, currentUserId, firstName, meta.goalsRewards, meta.serviceLane, activeAdvisorCount],
  )

  const w = stats.wallet
  const now = new Date()
  const daysToPayout = Math.max(
    0,
    Math.ceil((w.nextPayoutDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)),
  )
  const cadenceLabel =
    w.cadence === "weekly" ? "Weekly" : w.cadence === "biweekly" ? "Bi-weekly" : "Monthly"
  const payoutDateLabel = w.nextPayoutDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })

  const perEstimate =
    stats.effectiveGoals.rewards?.perEstimateSubmitted?.monetary?.enabled
      ? stats.effectiveGoals.rewards.perEstimateSubmitted.monetary.amount ?? 0
      : 0
  const perPaid =
    stats.effectiveGoals.rewards?.perEstimateCompleted?.monetary?.enabled
      ? stats.effectiveGoals.rewards.perEstimateCompleted.monetary.amount ?? 0
      : 0

  // Settled events only for the transaction feed — pending and lost rows
  // are surfaced via the bucket cards above, not duplicated in the feed.
  const settledEvents = useMemo(
    () => w.recentEvents.filter((e) => e.status === "settled"),
    [w.recentEvents],
  )
  const buckets = useMemo(() => bucketByDay(settledEvents, now), [settledEvents, now])

  // My-estimates status breakdown — Draft/Loading fold into Pending since
  // the advisor experiences both as "not yet picked up by the body shop."
  const myStatusCounts = useMemo(() => {
    const counts = { Pending: 0, Confirmed: 0, InProgress: 0, Scheduled: 0, Completed: 0, Declined: 0 }
    for (const e of estimates) {
      if (currentUserId == null || e.createdBy?.id !== currentUserId) continue
      const s = e.status === "Draft" || e.status === "Loading" ? "Pending" : e.status
      counts[s] += 1
    }
    return counts
  }, [estimates, currentUserId])

  const { data: payouts } = usePayouts(team?.id)
  const myPayouts = useMemo(
    () =>
      (payouts ?? [])
        .filter((p) => currentUserId != null && p.userId === currentUserId)
        .sort((a, b) => b.periodStart.localeCompare(a.periodStart)),
    [payouts, currentUserId],
  )

  const handleWithdraw = () => {
    toast.info("Withdraw rolling out soon — connect your payout method below to get notified.")
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-zinc-50 to-white pb-24">
      <div aria-hidden className="pointer-events-none fixed inset-0">
        <div className="absolute -top-24 right-[-120px] size-[520px] rounded-full bg-emerald-300/15 blur-3xl" />
      </div>

      <motion.div
        initial="hidden"
        animate="show"
        variants={{ show: { transition: STAGGER } }}
        className="relative max-w-[560px] mx-auto px-4 md:px-6 pt-4 md:pt-6 space-y-5"
      >
        {/* Top row — back chip + cadence eyebrow */}
        <motion.div variants={FADE_UP} className="flex items-center justify-between">
          <Link
            to={backHref}
            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-[12.5px] font-semibold text-zinc-500 hover:text-zinc-900"
          >
            <ArrowLeft className="size-4" aria-hidden />
            Back
          </Link>
          <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-zinc-200 bg-white text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-500">
            {cadenceLabel} payouts
          </span>
        </motion.div>

        {/* HERO — Available big card with Withdraw CTA */}
        <motion.div variants={FADE_UP} className="relative">
          <div
            aria-hidden
            className="absolute -inset-1 rounded-[28px] bg-gradient-to-br from-emerald-300/30 via-emerald-200/20 to-transparent blur-2xl"
          />
          <div className="relative overflow-hidden rounded-3xl border border-emerald-400/20 bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-900 shadow-2xl shadow-emerald-900/30">
            <div
              aria-hidden
              className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.12),transparent_60%)] pointer-events-none"
            />
            <Wallet01
              aria-hidden
              className="absolute -bottom-6 -right-6 size-40 text-white/[0.07] pointer-events-none"
            />

            <div className="relative p-6 md:p-7">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <div className="size-7 rounded-lg bg-white/15 backdrop-blur grid place-items-center">
                    <Wallet01 className="size-3.5 text-white" aria-hidden />
                  </div>
                  <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-emerald-100/80">
                    My wallet
                  </p>
                </div>
              </div>

              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-100/70">
                Available
              </p>
              <BigAmount
                value={w.available}
                className="text-[52px] md:text-[60px] font-black text-white leading-none tracking-tight tabular-nums mt-1.5"
              />
              <p className="text-[12.5px] text-emerald-50/80 mt-2">
                {daysToPayout === 0
                  ? "Auto-payout today"
                  : daysToPayout === 1
                    ? "Auto-payout tomorrow"
                    : `Auto-payout ${payoutDateLabel} (in ${daysToPayout} days)`}
              </p>

              <div className="mt-5 flex gap-2">
                <button
                  type="button"
                  onClick={handleWithdraw}
                  className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-2xl bg-white text-emerald-900 text-[13.5px] font-bold shadow-md active:scale-[0.99] hover:bg-emerald-50 transition-all"
                >
                  <CurrencyDollarCircle className="size-4" aria-hidden />
                  Withdraw
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Pending + Lost cards */}
        <motion.div variants={FADE_UP} className="grid grid-cols-2 gap-3">
          <BucketCard
            tone="amber"
            label="Pending"
            amount={w.pending}
            sub={
              w.pendingCount === 0
                ? "No open estimates"
                : `${w.pendingCount} open · $${perPaid} each if paid`
            }
            icon={Coins03}
          />
          <BucketCard
            tone="zinc"
            label="Lost"
            amount={w.lost}
            sub={
              w.lostCount === 0
                ? "No declined estimates"
                : `${w.lostCount} declined · customer didn't schedule`
            }
            icon={InfoCircle}
            muted
          />
        </motion.div>

        {/* My estimates — status breakdown with the per-paid $ at stake */}
        <motion.div
          variants={FADE_UP}
          className="rounded-2xl border border-zinc-200/70 bg-white shadow-sm overflow-hidden"
        >
          <div className="px-5 py-4">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-zinc-400 mb-1.5">
              My estimates
            </p>
            <StatusLine
              dot="bg-amber-400"
              label="Pending"
              count={myStatusCounts.Pending}
              right={myStatusCounts.Pending > 0 ? `$${(perPaid * myStatusCounts.Pending).toLocaleString("en-US")}` : null}
              rightClass="text-zinc-500"
            />
            <StatusLine
              dot="bg-blue-400"
              label="In progress"
              count={myStatusCounts.InProgress}
              right={myStatusCounts.InProgress > 0 ? `$${(perPaid * myStatusCounts.InProgress).toLocaleString("en-US")}` : null}
              rightClass="text-zinc-500"
            />
            <StatusLine
              dot="bg-sky-400"
              label="Scheduled"
              count={myStatusCounts.Scheduled}
              right={myStatusCounts.Scheduled > 0 ? `$${(perPaid * myStatusCounts.Scheduled).toLocaleString("en-US")}` : null}
              rightClass="text-zinc-500"
            />
            <StatusLine
              dot="bg-emerald-500"
              label="Completed"
              count={myStatusCounts.Completed}
              right={myStatusCounts.Completed > 0 ? `+$${(perPaid * myStatusCounts.Completed).toLocaleString("en-US")}` : null}
              rightClass="text-emerald-700"
            />
            <StatusLine
              dot="bg-red-400"
              label="Declined"
              count={myStatusCounts.Declined}
              right={myStatusCounts.Declined > 0 ? `$${(perPaid * myStatusCounts.Declined).toLocaleString("en-US")}` : null}
              rightClass="text-zinc-400 line-through"
            />
          </div>
        </motion.div>

        {/* Payout methods */}
        <motion.div
          variants={FADE_UP}
          className="rounded-2xl border border-zinc-200/70 bg-white shadow-sm overflow-hidden"
        >
          <div className="px-5 py-4">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-zinc-400 mb-3">
              Payout method
            </p>
            <PayoutMethodRow
              icon={Wallet01}
              tone="emerald"
              name="Venmo"
              handle="@anthony-m"
              isDefault
            />
            <PayoutMethodRow
              icon={Bank}
              tone="zinc"
              name="Connect IBAN"
              handle="For European bank accounts"
              connect
            />
          </div>
        </motion.div>

        {/* How you earn */}
        {(perEstimate > 0 || perPaid > 0) && (
          <motion.div
            variants={FADE_UP}
            className="rounded-2xl border border-zinc-200/70 bg-white shadow-sm overflow-hidden"
          >
            <div className="px-5 py-4">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-zinc-400 mb-3">
                How you earn
              </p>
              <div className="grid grid-cols-2 gap-3">
                <PayLine
                  icon={CheckSquare}
                  tone="emerald"
                  label="Per estimate"
                  amount={perEstimate}
                  sub="The moment you save it."
                />
                <PayLine
                  icon={Coins03}
                  tone="amber"
                  label="Per paid"
                  amount={perPaid}
                  sub="When the body shop completes it."
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Transactions feed */}
        <motion.div
          variants={FADE_UP}
          className="rounded-2xl border border-zinc-200/70 bg-white shadow-sm overflow-hidden"
        >
          <div className="px-5 py-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-zinc-400">
                Transactions
              </p>
              <Link
                to="/estimates"
                className="text-[11.5px] font-semibold text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"
              >
                See estimates
                <ArrowUpRight className="size-3" aria-hidden />
              </Link>
            </div>
            {buckets.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="space-y-4">
                {buckets.map((bucket) => (
                  <div key={bucket.label}>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5 px-1">
                      {bucket.label}
                    </p>
                    <div>
                      {bucket.events.map((ev, i) => (
                        <TxRow key={`${ev.occurredAt.getTime()}-${ev.trigger}-${i}`} event={ev} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* Payout history */}
        <motion.div
          variants={FADE_UP}
          className="rounded-2xl border border-zinc-200/70 bg-white shadow-sm overflow-hidden"
        >
          <div className="px-5 py-4">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-zinc-400 mb-3">
              Payout history
            </p>
            {myPayouts.length === 0 ? (
              <div className="rounded-xl border border-dashed border-zinc-200 bg-zinc-50/40 p-4">
                <div className="flex items-start gap-2.5">
                  <InfoCircle className="size-4 text-zinc-400 mt-0.5 shrink-0" aria-hidden />
                  <p className="text-[12.5px] text-zinc-500 leading-snug">
                    Past payouts appear here once your first cycle settles. Until
                    then, your accrued balance shows in Available above.
                  </p>
                </div>
              </div>
            ) : (
              <div>
                {myPayouts.map((p) => (
                  <PayoutHistoryRow key={p.id} record={p} />
                ))}
              </div>
            )}
          </div>
        </motion.div>

        <motion.div variants={FADE_UP} className="pt-2 text-center">
          <Link
            to={backHref}
            className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-zinc-500 hover:text-zinc-900"
          >
            ← Back to dashboard
          </Link>
        </motion.div>
      </motion.div>
    </div>
  )
}

/* ──────────────────── Helpers ──────────────────── */

interface DayBucket {
  label: string
  events: WalletEvent[]
}

function bucketByDay(events: WalletEvent[], now: Date): DayBucket[] {
  if (events.length === 0) return []
  const today = startOfDay(now)
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const out = new Map<string, DayBucket>()
  for (const ev of events) {
    const day = startOfDay(ev.occurredAt)
    const key = day.toISOString()
    let label: string
    if (day.getTime() === today.getTime()) label = "Today"
    else if (day.getTime() === yesterday.getTime()) label = "Yesterday"
    else
      label = day.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
      })

    if (!out.has(key)) out.set(key, { label, events: [] })
    out.get(key)!.events.push(ev)
  }
  return Array.from(out.values())
}

function startOfDay(d: Date): Date {
  const out = new Date(d)
  out.setHours(0, 0, 0, 0)
  return out
}

// "May 26 – Jun 8" from two ISO date strings. Parses the date part as
// local (plain `new Date("YYYY-MM-DD")` is UTC and shifts a day west of
// Greenwich).
function periodLabel(startIso: string, endIso: string): string {
  const fmt = (iso: string) => {
    const [y, m, d] = iso.slice(0, 10).split("-").map(Number)
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    })
  }
  return `${fmt(startIso)} – ${fmt(endIso)}`
}

/* ──────────────────── Atoms ──────────────────── */

function BigAmount({ value, className }: { value: number; className?: string }) {
  const mv = useMotionValue(0)
  const spring = useSpring(mv, { duration: 800, bounce: 0.05 })
  const display = useTransform(spring, (v) => Math.round(v).toLocaleString("en-US"))
  useEffect(() => {
    mv.set(value)
  }, [mv, value])
  return (
    <motion.span className={className}>
      <span aria-hidden>$</span>
      <motion.span>{display}</motion.span>
    </motion.span>
  )
}

const TONE_BG: Record<string, string> = {
  emerald: "from-emerald-400 to-emerald-600 shadow-emerald-500/30",
  amber: "from-amber-400 to-amber-600 shadow-amber-500/30",
  zinc: "from-zinc-300 to-zinc-500 shadow-zinc-400/30",
}

function BucketCard({
  tone,
  label,
  amount,
  sub,
  icon: Icon,
  muted = false,
}: {
  tone: "amber" | "zinc"
  label: string
  amount: number
  sub: string
  icon: React.ComponentType<{ className?: string }>
  muted?: boolean
}) {
  return (
    <div
      className={cx(
        "rounded-2xl border bg-white shadow-sm overflow-hidden p-4",
        tone === "amber" ? "border-amber-200/60" : "border-zinc-200/60",
      )}
    >
      <div className="flex items-center gap-2 mb-2">
        <div
          className={cx(
            "size-7 rounded-lg grid place-items-center shadow-md bg-gradient-to-br",
            TONE_BG[tone],
          )}
        >
          <Icon className="size-3.5 text-white" aria-hidden />
        </div>
        <p
          className={cx(
            "text-[10.5px] font-bold uppercase tracking-[0.14em]",
            tone === "amber" ? "text-amber-800" : "text-zinc-500",
          )}
        >
          {label}
        </p>
      </div>
      <p
        className={cx(
          "text-[28px] font-black leading-none tracking-tight tabular-nums",
          muted ? "text-zinc-500" : "text-zinc-900",
        )}
      >
        ${amount.toLocaleString("en-US")}
      </p>
      <p className="text-[11px] text-zinc-500 mt-2 leading-snug">{sub}</p>
    </div>
  )
}

function PayoutMethodRow({
  icon: Icon,
  tone,
  name,
  handle,
  isDefault = false,
  connect = false,
}: {
  icon: React.ComponentType<{ className?: string }>
  tone: "emerald" | "zinc"
  name: string
  handle: string
  isDefault?: boolean
  connect?: boolean
}) {
  const handleClick = () => {
    if (connect) {
      toast.info("Bank-account connection rolling out soon.")
    } else {
      toast.info("Payout-method management lands with the Tremendous integration.")
    }
  }
  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-zinc-50 transition-colors duration-100 text-left"
    >
      <div
        className={cx(
          "size-9 rounded-xl grid place-items-center shrink-0 shadow-md bg-gradient-to-br",
          TONE_BG[tone],
        )}
      >
        <Icon className="size-4 text-white" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-semibold text-zinc-900 leading-tight">{name}</p>
        <p className="text-[11.5px] text-zinc-500 leading-tight mt-0.5">{handle}</p>
      </div>
      {isDefault && (
        <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded-md text-[9.5px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800">
          Default
        </span>
      )}
      {connect && (
        <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg border border-zinc-200 bg-white text-[11.5px] font-semibold text-zinc-700">
          <Plus className="size-3" aria-hidden />
          Connect
        </span>
      )}
    </button>
  )
}

function PayLine({
  icon: Icon,
  tone,
  label,
  amount,
  sub,
}: {
  icon: React.ComponentType<{ className?: string }>
  tone: "emerald" | "amber"
  label: string
  amount: number
  sub: string
}) {
  return (
    <div className="rounded-xl border border-zinc-100 bg-zinc-50/40 p-3">
      <div className="flex items-center gap-2 mb-2">
        <div
          className={cx(
            "size-7 rounded-lg grid place-items-center shadow-md bg-gradient-to-br",
            TONE_BG[tone],
          )}
        >
          <Icon className="size-3.5 text-white" aria-hidden />
        </div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-zinc-700">{label}</p>
      </div>
      <p className="text-[22px] font-black text-zinc-900 leading-none tracking-tight tabular-nums">
        ${amount}
      </p>
      <p className="text-[11px] text-zinc-500 mt-1 leading-snug">{sub}</p>
    </div>
  )
}

function TxRow({ event }: { event: WalletEvent }) {
  const isPaid = event.trigger === "perEstimateCompleted"
  const isSubmit = event.trigger === "perEstimateSubmitted"
  const isCompetition = event.trigger in COMPETITION_DEFAULTS
  const isBonus = !isPaid && !isSubmit
  const isDup = event.duplicateOfVin === true
  const Icon = isPaid ? Coins03 : isSubmit ? ZapFast : isCompetition ? Trophy01 : Wallet01
  const time = event.occurredAt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
  const detailHref = event.estimateId ? `/estimates/${event.estimateId}` : null

  const inner = (
    <>
      <div
        className={cx(
          "size-9 rounded-xl grid place-items-center shrink-0 shadow-sm bg-gradient-to-br",
          isPaid
            ? TONE_BG.amber
            : isSubmit
              ? TONE_BG.emerald
              : "from-indigo-400 to-indigo-600 shadow-indigo-500/30",
        )}
      >
        <Icon className="size-4 text-white" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-semibold text-zinc-900 truncate">
          {event.label}
          {isDup && (
            <span className="ml-1.5 inline-flex items-center h-4 px-1.5 rounded align-middle bg-zinc-100 text-[9.5px] font-semibold text-zinc-500 whitespace-nowrap">
              duplicate — no payout
            </span>
          )}
        </p>
        <p className="text-[11.5px] text-zinc-400 tabular-nums">{time}</p>
      </div>
      <p
        className={cx(
          "text-[15px] font-bold tabular-nums shrink-0",
          isDup
            ? "text-zinc-400"
            : isBonus
              ? "text-indigo-700"
              : isPaid
                ? "text-amber-700"
                : "text-emerald-700",
        )}
      >
        {isDup ? "$0" : `+$${event.amount}`}
      </p>
    </>
  )

  return detailHref ? (
    <Link
      to={detailHref}
      className="flex items-center gap-3 px-1 py-2.5 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 -mx-1 px-1 rounded transition-colors duration-100"
    >
      {inner}
    </Link>
  ) : (
    <div className="flex items-center gap-3 px-1 py-2.5 border-b border-zinc-100 last:border-b-0">
      {inner}
    </div>
  )
}

function StatusLine({
  dot,
  label,
  count,
  right,
  rightClass,
}: {
  dot: string
  label: string
  count: number
  right: string | null
  rightClass: string
}) {
  return (
    <div className="flex items-center gap-2.5 px-1 py-2 border-b border-zinc-100 last:border-b-0">
      <span className={cx("size-2 rounded-full shrink-0", dot)} aria-hidden />
      <p className="text-[13px] font-semibold text-zinc-700">{label}</p>
      <span className="inline-flex items-center h-5 min-w-5 px-1.5 rounded-md bg-zinc-100 text-[11px] font-bold text-zinc-600 tabular-nums justify-center">
        {count}
      </span>
      <p
        className={cx(
          "ml-auto text-[13.5px] font-bold tabular-nums shrink-0",
          right === null ? "text-zinc-300" : rightClass,
        )}
      >
        {right ?? "—"}
      </p>
    </div>
  )
}

function PayoutHistoryRow({ record }: { record: PayoutRecord }) {
  const amount = record.approvedAmount ?? record.computedAmount
  return (
    <div className="flex items-center gap-3 px-1 py-2.5 border-b border-zinc-100 last:border-b-0">
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-semibold text-zinc-900">
          {periodLabel(record.periodStart, record.periodEnd)}
        </p>
        {record.note && (
          <p className="text-[11.5px] text-zinc-400 truncate mt-0.5">{record.note}</p>
        )}
      </div>
      {record.status === "paid" ? (
        <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded-md text-[9.5px] font-bold uppercase tracking-wider bg-emerald-600 text-white">
          <Check className="size-2.5" aria-hidden />
          Paid
        </span>
      ) : record.status === "approved" ? (
        <span className="inline-flex items-center h-5 px-1.5 rounded-md text-[9.5px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800">
          Approved
        </span>
      ) : (
        <span className="inline-flex items-center h-5 px-1.5 rounded-md text-[9.5px] font-bold uppercase tracking-wider bg-zinc-100 text-zinc-500">
          In review
        </span>
      )}
      <p className="text-[15px] font-bold tabular-nums text-zinc-900 shrink-0">
        ${amount.toLocaleString("en-US")}
      </p>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-zinc-200 bg-zinc-50/40 p-4">
      <div className="flex items-start gap-2.5">
        <InfoCircle className="size-4 text-zinc-400 mt-0.5 shrink-0" aria-hidden />
        <p className="text-[12.5px] text-zinc-500 leading-snug">
          No earning events yet. Write an estimate to seed your wallet.
        </p>
      </div>
    </div>
  )
}
